<template>
  <div class="mt-8">
    <woot-card class="p-5">
      <div class="flex flex-col items-start justify-end gap-3">
        <span class="text-sm font-medium">{{ $t('AI_SETTINGS.DATA_SYNC.TITLE') }}</span>

        <div class="w-64">
          <label class="sr-only">{{ $t('AI_SETTINGS.DATA_SYNC.TARGET.LABEL') }}</label>
          <select
            v-model="selectedTarget"
            class="px-3 py-2 w-full text-sm rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600"
          >
            <option v-for="opt in targetOptions" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
          </select>
        </div>
      </div>

      <div class="mt-5">
        <GoogleSheetsSyncCard 
          v-if="selectedTarget === 'google_sheets'" 
          ref="googleSheetsCard"
          :config="googleSheetsConfig"
          :layout-items="googleSheetsLayoutItems"
          @config-change="onGoogleSheetsConfigChange"
          @layout-change="onGoogleSheetsLayoutChange"
        />
        <LarkBitableSyncCard 
          v-else-if="selectedTarget === 'lark_bitable'" 
          ref="larkBitableCard"
          :config="larkBitableConfig"
          :layout-items="larkBitableLayoutItems"
          @config-change="onLarkBitableConfigChange"
          @layout-change="onLarkBitableLayoutChange"
        />
        <div v-else class="text-sm text-body-light py-3">
          {{ $t('AI_SETTINGS.DATA_SYNC.STATUS.DISABLED') }}
        </div>
      </div>

      <!-- Sync Mode Radios -->
      <div v-if="selectedTarget !== 'disabled'" class="mt-6">
        <div class="text-sm font-medium mb-2">{{ $t('AI_SETTINGS.DATA_SYNC.SYNC_MODE.TITLE') }}</div>
        <div class="flex flex-col gap-2 text-sm">
          <label class="inline-flex items-start gap-2 cursor-pointer">
            <input type="radio" class="mt-1" value="auto" v-model="syncMode" />
            <div>
              <div class="font-medium">{{ $t('AI_SETTINGS.DATA_SYNC.SYNC_MODE.OPTIONS.AUTO.LABEL') }}</div>
              <div class="text-body-light">{{ $t('AI_SETTINGS.DATA_SYNC.SYNC_MODE.OPTIONS.AUTO.DESC') }}</div>
            </div>
          </label>
          <label class="inline-flex items-start gap-2 cursor-pointer">
            <input type="radio" class="mt-1" value="when_required_ready" v-model="syncMode" />
            <div>
              <div class="font-medium">{{ $t('AI_SETTINGS.DATA_SYNC.SYNC_MODE.OPTIONS.REQUIRED.LABEL') }}</div>
              <div class="text-body-light">{{ $t('AI_SETTINGS.DATA_SYNC.SYNC_MODE.OPTIONS.REQUIRED.DESC') }}</div>
            </div>
          </label>
          <label class="inline-flex items-start gap-2 cursor-pointer">
            <input type="radio" class="mt-1" value="when_all_ready" v-model="syncMode" />
            <div>
              <div class="font-medium">{{ $t('AI_SETTINGS.DATA_SYNC.SYNC_MODE.OPTIONS.ALL.LABEL') }}</div>
              <div class="text-body-light">{{ $t('AI_SETTINGS.DATA_SYNC.SYNC_MODE.OPTIONS.ALL.DESC') }}</div>
            </div>
          </label>
        </div>
      </div>

      <div class="mt-4 flex justify-end">
        <woot-button variant="smooth" color-scheme="success" class="input-base" :is-loading="isSaving" @click="onSaveTarget">
          {{ $t('AI_SETTINGS.DATA_SYNC.SAVE_TARGET') }}
        </woot-button>
      </div>
    </woot-card>
  </div>
</template>

<script>
import DataSyncConfigAPI from 'dashboard/api/dataSyncConfig';
import DataSyncLayoutAPI from 'dashboard/api/dataSyncLayout';
import GoogleSheetsSyncCard from './GoogleSheetsSyncCard.vue';
import LarkBitableSyncCard from './LarkBitableSyncCard.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'DataSyncCard',
  components: { GoogleSheetsSyncCard, LarkBitableSyncCard },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      selectedTarget: 'disabled',
      syncMode: 'auto',
      googleSheetsConfig: {},
      googleSheetsLayoutItems: [],
      larkBitableConfig: {},
      larkBitableLayoutItems: [],
    };
  },
  computed: {
    targetOptions() {
      return [
        { id: 'disabled', name: this.$t('AI_SETTINGS.DATA_SYNC.TARGET.OPTIONS.DISABLED') },
        { id: 'google_sheets', name: this.$t('AI_SETTINGS.DATA_SYNC.TARGET.OPTIONS.GOOGLE_SHEETS') },
        { id: 'lark_bitable', name: this.$t('AI_SETTINGS.DATA_SYNC.TARGET.OPTIONS.LARK_BITABLE') },
      ];
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.isLoading = true;
      try {
        const { data } = await DataSyncConfigAPI.getConfig();
        this.selectedTarget = data?.target || 'disabled';
        this.syncMode = data?.sync_mode || 'auto';
        
        // Load config for each target
        this.googleSheetsConfig = this.extractGoogleSheetsConfig(data);
        this.larkBitableConfig = this.extractLarkBitableConfig(data);
        
        // Load layout for each target
        await this.loadLayouts();
      } catch (e) {
        // noop
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadLayouts() {
      try {
        // Load Google Sheets layout
        const googleSheetsLayout = await DataSyncLayoutAPI.getLayout('google_sheets');
        this.googleSheetsLayoutItems = (googleSheetsLayout.data.items || []).map((x, i) => ({ 
          ...x, 
          order_index: x.order_index ?? i 
        }));
        
        // Load Lark Bitable layout
        const larkBitableLayout = await DataSyncLayoutAPI.getLayout('lark_bitable');
        this.larkBitableLayoutItems = (larkBitableLayout.data.items || []).map((x, i) => ({ 
          ...x, 
          order_index: x.order_index ?? i 
        }));
      } catch (e) {
        // noop
      }
    },
    
    onGoogleSheetsConfigChange(config) {
      const sanitized = this.withoutTarget(config);
      this.googleSheetsConfig = { ...this.googleSheetsConfig, ...sanitized };
    },
    
    onGoogleSheetsLayoutChange(layoutItems) {
      this.googleSheetsLayoutItems = [...layoutItems];
    },
    
    onLarkBitableConfigChange(config) {
      const sanitized = this.withoutTarget(config);
      this.larkBitableConfig = { ...this.larkBitableConfig, ...sanitized };
    },
    
    onLarkBitableLayoutChange(layoutItems) {
      this.larkBitableLayoutItems = [...layoutItems];
    },
    
    async onSaveTarget() {
      this.isSaving = true;
      try {
        const payload = { target: this.selectedTarget };
        
        if (this.selectedTarget === 'google_sheets') {
          const googleCardValid = await this.$refs.googleSheetsCard?.validateForm?.();
          if (googleCardValid === false) {
            return;
          }
          Object.assign(payload, this.googleSheetsConfig);
          payload.layout_items = this.googleSheetsLayoutItems;
        } else if (this.selectedTarget === 'lark_bitable') {
          Object.assign(payload, this.larkBitableConfig);
          payload.layout_items = this.larkBitableLayoutItems;
        }

        payload.sync_mode = this.syncMode;
        payload.target = this.selectedTarget;
        
        await DataSyncConfigAPI.updateConfig(payload);
        useAlert(this.$t('AI_SETTINGS.DATA_SYNC.SAVE_SUCCESS'), { type: 'success' });
      } catch (e) {
        const errorMessage = this.getFirstFieldErrorMessage(
          e?.response?.data
        );
        useAlert(errorMessage, { type: 'error' });
      } finally {
        this.isSaving = false;
      }
    },
    getFirstFieldErrorMessage(errorData = {}) {
      // Check for simple error field first (e.g., "Feature not available on current plan")
      if (errorData?.error) {
        return errorData.error;
      }
      
      const defaultMessage = errorData?.message || this.$t('GENERAL_ERROR');
      const errors = errorData?.errors;
      if (!errors || typeof errors !== 'object') {
        return defaultMessage;
      }
      for (const key of Object.keys(errors)) {
        const value = errors[key];
        if (Array.isArray(value)) {
          const message = value.find(Boolean);
          if (message) {
            return message;
          }
        } else if (value) {
          return value;
        }
      }
      return defaultMessage;
    },
    extractGoogleSheetsConfig(data = {}) {
      return {
        google_spreadsheet_id: data.google_spreadsheet_id || '',
        google_sheet_name: data.google_sheet_name || '',
        google_credentials: data.google_credentials || '',
      };
    },
    
    extractLarkBitableConfig(data = {}) {
      return {
        lark_api_host: data.lark_api_host || 'open.larksuite.com',
        lark_app_id: data.lark_app_id || '',
        lark_app_secret: data.lark_app_secret || '',
        lark_app_token: data.lark_app_token || '',
        lark_table_id: data.lark_table_id || '',
        lark_create_missing_fields: data.lark_create_missing_fields ?? true,
        lark_enable_attachments: data.lark_enable_attachments ?? false,
      };
    },
    
    withoutTarget(config = {}) {
      if (!config || typeof config !== 'object') {
        return {};
      }
      const { target, ...rest } = config;
      return rest;
    },
  },
};
</script>

